import { FormEvent, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { AuthLayout } from '@/components/auth/AuthLayout';
import { OAuthButtons } from '@/components/auth/OAuthButtons';
import {
  TextField, PasswordField, OrDivider, FormError, SubmitButton,
} from '@/components/auth/FormFields';
import { authApi, readApiError } from '@/lib/auth-api';

export function LoginPage() {
  const { t } = useTranslation();
  const nav = useNavigate();
  const qc = useQueryClient();
  const [params] = useSearchParams();
  const failNote =
    params.get('auth') === 'oauth_google_failed' ? (t('auth.error_google_failed', { defaultValue: 'Google sign-in failed. Please try again.' }) as string)
    : params.get('auth') === 'oauth_apple_failed' ? (t('auth.error_apple_failed', { defaultValue: 'Apple sign-in failed. Please try again.' }) as string)
    : params.get('auth') === 'required' ? (t('auth.session_expired', { defaultValue: 'Please sign in to continue.' }) as string)
    : null;

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [pending, setPending] = useState(false);
  const [err, setErr] = useState<string | null>(failNote);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setErr(null);
    setPending(true);
    try {
      await authApi.login({ email, password });
      qc.invalidateQueries({ queryKey: ['auth', 'me'] });
      nav('/app', { replace: true });
    } catch (caught) {
      setErr(readApiError(caught, t('auth.error_invalid_credentials', { defaultValue: 'Invalid email or password.' }) as string));
    } finally {
      setPending(false);
    }
  }

  return (
    <AuthLayout
      title={t('auth.login_title', { defaultValue: 'Welcome back.' })}
      subtitle={t('auth.login_subtitle', {
        defaultValue: 'Sign in to your knowledge plane. Every document, every signal, every thought — waiting.',
      })}
      footer={
        <span>
          {t('auth.no_account', { defaultValue: 'New to Knowlez?' })}{' '}
          <Link to="/signup" className="text-accent-700 font-semibold hover:underline">
            {t('auth.signup_cta', { defaultValue: 'Create a free workspace' })}
          </Link>
        </span>
      }
    >
      <OAuthButtons />
      <OrDivider label={t('auth.or_email', { defaultValue: 'or with email' }) as string} />

      <form onSubmit={onSubmit} className="space-y-4">
        <FormError message={err} />
        <TextField
          id="email"
          type="email"
          inputMode="email"
          label={t('auth.email_label', { defaultValue: 'Email' }) as string}
          placeholder="you@company.com"
          value={email}
          onChange={setEmail}
          autoComplete="email"
          autoFocus
          required
        />
        <div>
          <PasswordField
            id="password"
            label={t('auth.password_label', { defaultValue: 'Password' }) as string}
            value={password}
            onChange={setPassword}
            autoComplete="current-password"
            required
          />
          <Link
            to="/forgot-password"
            className="mt-2 inline-block text-[12.5px] text-accent-700 hover:underline"
          >
            {t('auth.forgot_password', { defaultValue: 'Forgot password?' })}
          </Link>
        </div>
        <SubmitButton pending={pending}>
          {t('auth.sign_in', { defaultValue: 'Sign in' })}
        </SubmitButton>
      </form>
    </AuthLayout>
  );
}
